import Mustache from "mustache"
import sampleSize from "lodash/sampleSize"
import { recommendationTemplate } from "./templates"
import { loadUserRecentSearches } from "app/components/universal_search/search_history"
import { TOTAL_MAX_RECENT_SEARCHES } from "./recent_searches"

export interface Service {
  recommended: boolean
  image: string
  [key: string]: unknown
}

const processServicesResponse = (services: Service[], totalRecomendations: number): void => {
  const recommendedServices = services.filter((service: Service) => service.recommended)
  const recommendationSample = sampleSize(recommendedServices, totalRecomendations)

  recommendationSample.forEach((service: Service) => {
    const link = document.createElement("link")
    link.rel = "preload"
    link.as = "image"
    link.href = service.image
    document.head.appendChild(link)
  })

  document.querySelectorAll(".recommended-services > ul.universal-search-helper-list").forEach(recommendedWrapper => {
    recommendedWrapper.innerHTML = ""
    recommendationSample.forEach((recommendation: Service) => {
      const rendered = Mustache.render(recommendationTemplate, recommendation)
      recommendedWrapper.insertAdjacentHTML("beforeend", rendered)
    })
  })
}

// This component is responsible to load and display recommended services
export const RecommendedServices = async (): Promise<Service[]> => {
  const totalRecomendations = 8 - (loadUserRecentSearches().slice(0, TOTAL_MAX_RECENT_SEARCHES).length || 0)
  let allServices: Service[] = []

  const cacheBaseName = "recommended-services-cache"
  const cacheName = `${cacheBaseName}-${new Date().toISOString().slice(0, 10)}`
  const requestUrl = "/explore/services.json"

  const fetchAndProcessServices = async (response: Response) => {
    if (response.ok) {
      allServices = await response.json()
      processServicesResponse(allServices, totalRecomendations)
    }
  }

  const fetchWithFallback = async (): Promise<void> => {
    // The Cache API is best-effort: it can be unavailable (e.g. Safari private
    // mode throws on caches.open). Isolate it in its own try so a cache failure
    // falls through to a plain fetch — WITHOUT swallowing a later network error.
    let cache: Cache | undefined
    try {
      // Best-effort cache cleanup: swallow rejections so a failing delete/keys
      // call never surfaces as an unhandled rejection outside this try.
      void caches.delete(cacheBaseName).catch(() => {})
      void caches
        .keys()
        .then(keys =>
          keys
            .filter(key => key.startsWith(cacheBaseName) && key !== cacheName)
            .forEach(key => void caches.delete(key).catch(() => {})),
        )
        .catch(() => {})

      cache = await caches.open(cacheName)
      const cachedResponse = await cache.match(requestUrl)
      if (cachedResponse) {
        await fetchAndProcessServices(cachedResponse)
        return
      }
    } catch {
      // Cache API unavailable — fall through to a direct fetch.
      cache = undefined
    }

    // Exactly one network request. A failure here (commonly a fetch aborted by
    // navigation on short-lived pages) is not retried: the retry would repeat a
    // request that is already doomed and just double the work. The rejection
    // propagates to the caller, which reports it.
    const networkResponse = await fetch(requestUrl)
    if (cache && networkResponse.ok) {
      // Best-effort cache write; a failure here must not block rendering.
      void cache.put(requestUrl, networkResponse.clone()).catch(() => {})
    }
    await fetchAndProcessServices(networkResponse)
  }

  try {
    await fetchWithFallback()
  } catch (fallbackError) {
    window.App?.Sentry?.captureException(fallbackError, {
      tags: {
        component: "RecommendedServices",
        action: "fetch",
      },
    })
  }

  return allServices
}
